import type { IObservableArray } from 'mobx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, IGeoSetTargetable, Targeting } from '@feathr/blackbox';
import { TargetableClass } from '@feathr/blackbox';
import { showError } from '@feathr/components';
import { useModals } from '@feathr/extender/components';
import { useStore } from '@feathr/extender/state';
import { useAccount } from '@feathr/report_components';

import { getGeoTargetings, removeOrArchiveTargetings } from '../../Targetings.utils';
import type { IGeoFilterSetData } from '../GeoFilterModal';
import { useGeoFilterSetOperations } from '../Locations.useGeoFilterSetOperations';
import type { IGeoFilterSetOption } from './GeoFilterSet.utils';
import { sortGeoFilterSets } from './GeoFilterSet.utils';

/**
 * Props for the useGeoFilterSet hook.
 */
export interface IUseGeoFilterSetProps {
  campaign: Campaign;
  targetings: IObservableArray<Targeting>;
  defaultGeoSetId?: string;
  hasUserDeclinedGeofilters?: boolean;
  onEditGeoFilterSet?: (geoFilterSet: IGeoSetTargetable) => void;
}

/**
 * Return type for the useGeoFilterSet hook.
 */
interface IUseGeoFilterSetReturn {
  /** The currently selected geofilter set */
  selectedGeoFilterSet: IGeoFilterSetOption | null;
  /** Sorted list of available geofilter set options */
  sortedGeoFilterSets: IGeoFilterSetOption[];
  /** Whether the geofilter sets are loading */
  isLoading: boolean;
  /** Modal state for edit/create */
  editCreate: ReturnType<typeof useModals>['editCreate'];
  /** Handler to clear the current selection */
  handleClearSelection: () => void;
  /** Handler when a geofilter set is selected */
  handleSetSelection: (geoFilterSet?: IGeoFilterSetOption) => Promise<void>;
  /** Handler to submit edits to a geofilter set */
  handleEditGeoFilterSetSubmit: (updatedData: IGeoFilterSetData) => Promise<void>;
}

/**
 * Custom hook that manages all state and logic for the GeoFilterSet component.
 *
 * Handles:
 * - Loading and listing available geofilter sets
 * - Tracking the currently selected geofilter set
 * - Syncing selection with existing campaign targetings
 * - Applying and updating geofilter sets
 *
 * @param props - Configuration props
 * @returns Object containing state values and handlers
 *
 * @example
 * ```tsx
 * const {
 *   selectedGeoFilterSet,
 *   sortedGeoFilterSets,
 *   handleSetSelection,
 * } = useGeoFilterSet({ campaign, targetings });
 * ```
 */
export function useGeoFilterSet({
  campaign,
  targetings,
  defaultGeoSetId,
  hasUserDeclinedGeofilters = false,
}: IUseGeoFilterSetProps): IUseGeoFilterSetReturn {
  const { t } = useTranslation();
  const { Targetables, Targetings } = useStore();
  const account = useAccount();
  const { editCreate } = useModals();

  const { applyGeoFilterSet, updateGeoFilterSet } = useGeoFilterSetOperations({
    campaign,
    targetings,
  });

  const geoSetTargetables = Targetables.list({
    filters: {
      is_archived__ne: true,
      _cls: TargetableClass.geo_set,
    },
  });

  const [selectedGeoFilterSet, setSelectedGeoFilterSet] = useState<IGeoFilterSetOption | null>(
    null,
  );

  const geoFilterSetOptions: IGeoFilterSetOption[] = geoSetTargetables.models.map((targetable) => ({
    id: targetable.id,
    _cls: TargetableClass.geo_set,
    name: targetable.get('name'),
    included_geo_filters: targetable.get('included_geo_filters') ?? [],
    excluded_geo_filters: targetable.get('excluded_geo_filters') ?? [],
    is_default: targetable.id === defaultGeoSetId,
  }));

  useSyncSelectedGeoFilterSet({
    targetings,
    geoSetTargetablesIsPending: geoSetTargetables.isPending,
    geoFilterSetOptions,
    defaultGeoSetId,
    hasUserDeclinedGeofilters,
    isNewCampaign: campaign.isEphemeral,
    applyGeoFilterSet,
    setSelectedGeoFilterSet,
  });

  const handleClearSelection = useCallback((): void => {
    setSelectedGeoFilterSet(null);
    const currentGeoTargetings = getGeoTargetings(targetings);
    removeOrArchiveTargetings({
      targetingsToRemove: currentGeoTargetings,
      targetingsArray: targetings,
      Targetings,
    });
  }, [targetings, Targetings]);

  const handleSetSelection = useCallback(
    async (geoFilterSet?: IGeoFilterSetOption): Promise<void> => {
      setSelectedGeoFilterSet(geoFilterSet ?? null);

      if (!geoFilterSet) {
        const currentGeoTargetings = getGeoTargetings(targetings);
        removeOrArchiveTargetings({
          targetingsToRemove: currentGeoTargetings,
          targetingsArray: targetings,
          Targetings,
        });
        return;
      }

      try {
        await applyGeoFilterSet(geoFilterSet.id);
      } catch (error) {
        showError(error, t);
      }
    },
    [applyGeoFilterSet, t, targetings, Targetings],
  );

  const handleEditGeoFilterSetSubmit = useCallback(
    async (updatedData: IGeoFilterSetData): Promise<void> => {
      if (!selectedGeoFilterSet) {
        return;
      }

      await updateGeoFilterSet(selectedGeoFilterSet.id, updatedData);

      const wasDefault = selectedGeoFilterSet.id === defaultGeoSetId;
      if (updatedData.is_default && !wasDefault) {
        account.setSetting('default_geo_set', selectedGeoFilterSet.id);
        await account.patchDirty();
      } else if (!updatedData.is_default && wasDefault) {
        account.setSetting('default_geo_set', undefined);
        await account.patchDirty();
      }

      setSelectedGeoFilterSet({
        ...selectedGeoFilterSet,
        name: updatedData.name,
        is_default: updatedData.is_default,
        included_geo_filters: updatedData.included_geo_filters,
        excluded_geo_filters: updatedData.excluded_geo_filters,
      });
    },
    [selectedGeoFilterSet, defaultGeoSetId, account, updateGeoFilterSet],
  );

  return {
    selectedGeoFilterSet,
    sortedGeoFilterSets: sortGeoFilterSets(geoFilterSetOptions),
    isLoading: geoSetTargetables.isPending,
    editCreate,
    handleClearSelection,
    handleSetSelection,
    handleEditGeoFilterSetSubmit,
  };
}

/**
 * Internal hook to sync the selected geofilter set with existing campaign targetings.
 * Also handles auto-selecting the default geofilter set for new campaigns.
 */
function useSyncSelectedGeoFilterSet({
  targetings,
  geoSetTargetablesIsPending,
  geoFilterSetOptions,
  defaultGeoSetId,
  hasUserDeclinedGeofilters,
  isNewCampaign,
  applyGeoFilterSet,
  setSelectedGeoFilterSet,
}: {
  targetings: IObservableArray<Targeting>;
  geoSetTargetablesIsPending: boolean;
  geoFilterSetOptions: IGeoFilterSetOption[];
  defaultGeoSetId?: string;
  hasUserDeclinedGeofilters: boolean;
  isNewCampaign: boolean;
  applyGeoFilterSet: (geoSetId: string) => Promise<void>;
  setSelectedGeoFilterSet: (option: IGeoFilterSetOption | null) => void;
}): void {
  const { Targetables } = useStore();
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (hasInitializedRef.current) {
      return;
    }

    // First, try to sync from existing geo_set targeting immediately
    // This works even if geoSetTargetables is pending because Targetables.get() uses the store directly
    const currentGeoTargeting = getGeoTargetings(targetings).find((t) => {
      const targetableId = t.get('target_data');
      if (!targetableId) {
        return false;
      }

      const targetable = Targetables.get(targetableId);
      return targetable?.get('_cls') === TargetableClass.geo_set;
    });

    if (currentGeoTargeting) {
      const targetDataId = currentGeoTargeting.get('target_data');
      if (!targetDataId) {
        return;
      }

      const targetable = Targetables.get(targetDataId);

      if (targetable) {
        hasInitializedRef.current = true;
        setSelectedGeoFilterSet({
          id: targetable.id,
          _cls: TargetableClass.geo_set,
          name: targetable.get('name'),
          included_geo_filters: targetable.get('included_geo_filters') ?? [],
          excluded_geo_filters: targetable.get('excluded_geo_filters') ?? [],
          is_default: targetable.id === defaultGeoSetId,
        });
        return;
      }
    }

    // Wait for geoSetTargetables to load before trying default set logic
    if (geoSetTargetablesIsPending) {
      return;
    }

    hasInitializedRef.current = true;

    // Auto-select default set for new campaigns with no geo targetings
    if (defaultGeoSetId && !hasUserDeclinedGeofilters && isNewCampaign) {
      const geoKinds = new Set(['geo', 'geo_set']);
      const hasNoGeoTargetings = targetings.filter((t) => geoKinds.has(t.get('kind'))).length === 0;
      if (hasNoGeoTargetings) {
        const defaultSet = geoFilterSetOptions.find((opt) => opt.id === defaultGeoSetId);
        if (defaultSet) {
          setSelectedGeoFilterSet(defaultSet);
          applyGeoFilterSet(defaultGeoSetId).catch(() => {
            setSelectedGeoFilterSet(null);
          });
        }
      }
    }
  }, [
    geoSetTargetablesIsPending,
    targetings,
    Targetables,
    geoFilterSetOptions,
    defaultGeoSetId,
    hasUserDeclinedGeofilters,
    isNewCampaign,
    applyGeoFilterSet,
    setSelectedGeoFilterSet,
  ]);
}
